import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

const DROPDOWN_HEIGHT = 280;
const MARGIN = 8;

/**
 * Generic anchor-relative floating panel.
 *
 * Handles positioning, framer-motion open/close animation, outside-click
 * dismissal, and ESC key dismissal. Renders via createPortal so z-index
 * stacking is never affected by ancestor overflow or z-index contexts.
 *
 * @param {object} props
 * @param {React.RefObject} props.anchorRef   - Ref on the trigger element (button/icon)
 * @param {boolean}         props.isOpen      - Controlled open state
 * @param {function}        props.onClose     - Called when panel should close
 * @param {number}          [props.width=256] - Panel width in px
 * @param {string}          [props.align='left'] - 'left' aligns panel to anchor's left edge; 'right' aligns to anchor's right edge
 * @param {string}          [props.className] - Extra classes applied to the panel div
 * @param {React.ReactNode} props.children    - Panel content
 */
const AnchoredPanel = ({
  anchorRef,
  isOpen,
  onClose,
  width = 256,
  align = "left",
  className = "",
  children,
}) => {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    useRight: false,
    openDownward: false,
  });

  // Recompute position each time the panel opens, and again on window resize/
  // scroll while it's open — without this, resizing the window (or scrolling
  // a page that moves the anchor) left the panel stuck at its stale
  // open-time coordinates, detached from its anchor or overflowing the
  // viewport. Every other positioned dropdown in the app (e.g.
  // Dropdowns/index.js's calculateDropDirection) already recomputes on resize;
  // this shared component was the one place that didn't.
  useEffect(() => {
    if (!isOpen || !anchorRef?.current) return;

    const computePosition = () => {
      if (!anchorRef?.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openDownward =
        spaceBelow >= DROPDOWN_HEIGHT || spaceBelow > rect.top;
      const margin = MARGIN;
      if (align === "right") {
        // Right-align: panel's right edge = anchor's right edge.
        // Clamp so panel doesn't overflow either viewport edge.
        const rightFromViewport = window.innerWidth - rect.right;
        const maxRight = Math.max(margin, window.innerWidth - width - margin);
        setPos({
          openDownward,
          top: rect.bottom + 4,
          bottom: window.innerHeight - rect.top + 4,
          right: Math.max(margin, Math.min(rightFromViewport, maxRight)),
          left: 0,
          useRight: true,
        });
      } else {
        // Left-align: panel's left edge = anchor's left edge, clamped to viewport
        const clampedLeft = Math.min(rect.left, window.innerWidth - width - margin);
        setPos({
          openDownward,
          top: rect.bottom + 4,
          bottom: window.innerHeight - rect.top + 4,
          left: Math.max(margin, clampedLeft),
          right: 0,
          useRight: false,
        });
      }
    };

    computePosition();
    window.addEventListener("resize", computePosition);
    window.addEventListener("scroll", computePosition, true);
    return () => {
      window.removeEventListener("resize", computePosition);
      window.removeEventListener("scroll", computePosition, true);
    };
  }, [isOpen, anchorRef, width, align]);

  // Outside-click dismissal (excludes anchor button so toggle works)
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen, anchorRef, onClose]);

  // ESC key dismissal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: pos.openDownward ? -10 : 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: pos.openDownward ? -10 : 10 }}
          transition={{ duration: 0.15 }}
          style={{
            position: "fixed",
            ...(pos.openDownward
              ? { top: `${pos.top}px` }
              : { bottom: `${pos.bottom}px` }),
            ...(pos.useRight
              ? { right: `${pos.right}px` }
              : { left: `${pos.left}px` }),
            width: `min(${width}px, calc(100vw - ${MARGIN * 2}px))`,
            zIndex: 9999,
          }}
          className={`bg-surface-panel border border-edge-gray-200 dark:border-edge-panel-border rounded-lg shadow-lg ${className}`}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AnchoredPanel;
